/**
 * Copy and small formatters for the Decisions surface. Concepts are firm,
 * wording is loose: nothing here is a locked customer line, but every label
 * follows CONTEXT.md (Team, Worker, Decision ledger; never "mined", never
 * "verified by AI"; no em-dashes).
 */
import type { CodeDecision } from "@yagni/shared";
import type { DecisionsView } from "./decisionsApi.js";

/**
 * A decision's provenance line: where the judgment came from. These are the
 * values the backend actually writes. Anything unrecognised passes through as
 * itself, so a new source is visibly unstyled rather than silently mislabelled.
 */
export function sourceLabel(source: string): string {
  switch (source) {
    case "record_tool":
      return "Recorded by the agent";
    case "cli_correction":
      return "Captured from a correction";
    case "onboarding_seed":
      return "Drafted during onboarding";
    case "onboarding_confirm":
      return "Confirmed during onboarding";
    case "library_edit":
      return "Recorded by a teammate";
    case "repo_mined":
      return "Read from the repo's docs";
    case "plan_gate_defense":
      return "Defended at the plan gate";
    case "plan_gate_answer":
      return "Answered at the plan gate";
    case "plan_gate_reeve_decision":
      return "Decided by Reeve at the plan gate";
    // Retired writer (an accepted plan change is a diff, not a judgment), kept
    // because rows recorded under it are still on the ledger.
    case "plan_gate_amendment":
      return "Amended at the plan gate";
    case "connector_mined":
      return "Read from a connected tool";
    default:
      return source;
  }
}

/**
 * What sort of call a decision is (`kind`, additive). Null on rows recorded
 * before kinds existed and on human-authored rows; those render no label.
 */
export function kindLabel(kind: string | null | undefined): string | null {
  switch (kind) {
    case "product":
      return "Product call";
    case "architecture":
      return "Architecture call";
    case "convention":
      return "Team convention";
    default:
      return kind ? kind : null;
  }
}

export function formatDecisionDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/** "3 days ago" for the list rows; falls back to the date past a month. */
export function relativeDate(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const days = Math.floor((now.getTime() - date.getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return formatDecisionDate(iso);
}

export function truncate(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

/** A decision this short is its own headline; nothing is split off. */
export const HEADLINE_SPLIT_MIN_CHARS = 240;
/** Sentences accumulate into the headline until it is at least this long, so "No." never leads. */
const HEADLINE_MIN_CHARS = 60;
/** Past this the headline is a paragraph again, so the split is abandoned. */
const HEADLINE_MAX_CHARS = 320;

/**
 * A sentence boundary: terminal punctuation followed by whitespace and an
 * opener (capital, digit, quote, bracket, backtick), or a line break. The
 * lowercase-after-period case (`ONLY (<main>/.git). denyWrite pins ...`) is
 * deliberately not a boundary, since it is more often a path or an
 * identifier than a sentence end.
 */
const SENTENCE_BOUNDARY = /(?<=[.!?:])\s*\n+\s*|(?<=[.!?])\s+(?=["'`([\dA-Z])/g;

/**
 * Split a long decision into the sentence(s) that carry the ruling and the
 * remainder. Rendering only: the stored text never changes. Rows recorded
 * before the authoring contract asked for a one- or two-sentence decision
 * carry their reasoning inside the decision field, and a 500-character
 * paragraph in headline type is unreadable. Short decisions come back whole,
 * and the remainder keeps its own line breaks.
 */
export function splitDecisionHeadline(text: string): { headline: string; rest: string | null } {
  const whole = text.trim();
  if (whole.length <= HEADLINE_SPLIT_MIN_CHARS) return { headline: whole, rest: null };
  const boundary = new RegExp(SENTENCE_BOUNDARY.source, "g");
  let match: RegExpExecArray | null;
  while ((match = boundary.exec(whole)) !== null) {
    if (match.index < HEADLINE_MIN_CHARS) continue;
    if (match.index > HEADLINE_MAX_CHARS) break;
    const headline = whole.slice(0, match.index).trimEnd();
    const rest = whole.slice(match.index + match[0].length).trim();
    if (rest.length === 0) break;
    return { headline, rest };
  }
  return { headline: whole, rest: null };
}

export type Standing = "confirmed" | "assumption" | "superseded" | "discarded";

export function standingOf(decision: Pick<CodeDecision, "status" | "verification">): Standing {
  if (decision.status === "superseded") return "superseded";
  if (decision.status === "discarded") return "discarded";
  return decision.verification === "confirmed" ? "confirmed" : "assumption";
}

export const STANDING_LABEL: Record<Standing, string> = {
  confirmed: "Confirmed",
  assumption: "Unconfirmed",
  superseded: "Superseded",
  discarded: "Discarded",
};

export const VIEW_LABEL: Record<DecisionsView, string> = {
  all: "All",
  needs_review: "Needs review",
  confirmed: "Confirmed",
  history: "History",
};

export function citedLabel(count: number): string {
  if (count <= 0) return "Not cited yet";
  return `Cited ${count} ${count === 1 ? "time" : "times"}`;
}

/** The scope line: workspace-wide, or the repository and the Teams that attach it. */
export function scopeLabel(decision: Pick<CodeDecision, "repoFullName" | "teams">): string {
  if (!decision.repoFullName) return "Workspace-wide";
  const teams = decision.teams ?? [];
  if (teams.length === 0) return decision.repoFullName;
  return `${teams.map((t) => t.name).join(", ")} · ${decision.repoFullName}`;
}

export const SURFACE_INTRO =
  "What the Teams decided and why, captured as the work happened. Every Agent reads this before it guesses; a confirmed decision outweighs an assumption.";

export const EMPTY_TITLE = "No decisions recorded yet.";

export const EMPTY_WAYS: Array<{ title: string; detail: string }> = [
  {
    title: "As developers work",
    detail:
      "YAGNI Code records the product calls made in a session and banks a correction the moment a developer confirms it.",
  },
  {
    title: "As the Teams work",
    detail:
      "Approving a plan on Work ratifies the points the Agent defended; a fresh repository can seed the ledger from its own docs.",
  },
  {
    title: "By hand",
    detail: "Record a decision here when a call is made in a meeting or a thread and should outlive it.",
  },
];
